import styleInject from 'style-inject';
import React, { forwardRef } from 'react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import { z } from 'zod';
import 'lodash-es';
import 'md5';

var css_248z$8 = "/* Initially added with PR #253 for each component, this is now a global setting\n * It is recommended on MDN to use this as a default for layouting.\n*/\n* {\n  box-sizing: border-box;\n}\n";
styleInject(css_248z$8);

/**
 * These modes are ONLY intended to be internally used within the context of
 * editing an experience inside of Contentful Studio. i.e. these modes
 * intentionally do not include preview/delivery modes.
 */
var StudioCanvasMode$1;
(function (StudioCanvasMode) {
    StudioCanvasMode["READ_ONLY"] = "readOnlyMode";
    StudioCanvasMode["EDITOR"] = "editorMode";
    StudioCanvasMode["NONE"] = "none";
})(StudioCanvasMode$1 || (StudioCanvasMode$1 = {}));
const CONTENTFUL_COMPONENT_CATEGORY = 'contentful-component';
const CONTENTFUL_DEFAULT_CATEGORY = 'Contentful';
const CONTENTFUL_COMPONENTS$1 = {
    section: {
        id: 'contentful-section',
        name: 'Section',
    },
    container: {
        id: 'contentful-container',
        name: 'Container',
    },
    columns: {
        id: 'contentful-columns',
        name: 'Columns',
    },
    singleColumn: {
        id: 'contentful-single-column',
        name: 'Column',
    },
    button: {
        id: 'contentful-button',
        name: 'Button',
    },
    heading: {
        id: 'contentful-heading',
        name: 'Heading',
    },
    image: {
        id: 'contentful-image',
        name: 'Image',
    },
    richText: {
        id: 'contentful-richText',
        name: 'Rich Text',
    },
    text: {
        id: 'contentful-text',
        name: 'Text',
    },
    divider: {
        id: 'contentful-divider',
        name: 'Divider',
    },
    carousel: {
        id: 'contentful-carousel',
        name: 'Carousel',
    },
};
var PostMessageMethods$1;
(function (PostMessageMethods) {
    PostMessageMethods["REQUEST_ENTITIES"] = "REQUEST_ENTITIES";
    PostMessageMethods["REQUESTED_ENTITIES"] = "REQUESTED_ENTITIES";
})(PostMessageMethods$1 || (PostMessageMethods$1 = {}));

function combineClasses(...classes) {
    return classes
        .filter((val) => val)
        .map((c) => c.trim())
        .join(' ');
}

var css_248z$7 = "/* If the label is empty, still render the normal height by adding this pseudo element */\n.cf-button:empty::before {\n  content: '';\n  display: inline-block;\n}\n";
styleInject(css_248z$7);

const Button = ({ children, className, label, onClick, onNavigate, target, url, ...props }) => {
    const classes = combineClasses('cf-button', className);
    const handleClick = (event) => {
        if (onNavigate && url) {
            event.preventDefault();
            onNavigate(url, target);
        }
        onClick && onClick(event);
    };
    const button = (React.createElement("button", { className: classes, "data-url": url, "data-target": target, onClick: handleClick, ...props }, label ? label : children));
    // FIXME: Wrapping <button> with <a> is not ARIA conform. Instead, <a> should look like a button.
    return url ? (React.createElement("a", { href: url, target: target }, button)) : (button);
};

const ButtonComponentDefinition = {
    id: CONTENTFUL_COMPONENTS$1.button.id,
    name: CONTENTFUL_COMPONENTS$1.button.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    builtInStyles: [
        'cfMargin',
        'cfMaxWidth',
        'cfLetterSpacing',
        'cfTextItalic',
        'cfTextUnderline',
        'cfTextBold',
        'cfLineHeight',
        'cfBorder',
        'cfBorderRadius',
    ],
    tooltip: {
        description: 'Drop onto the canvas to add button that can be used to trigger an action.',
    },
    variables: {
        cfFontSize: {
            displayName: 'Font Size',
            type: 'Text',
            group: 'style',
            description: 'The font size of the button.',
            defaultValue: '16px',
        },
        cfFontWeight: {
            displayName: 'Font Weight',
            type: 'Text',
            group: 'style',
            description: 'The font weight of the button.',
            defaultValue: '600',
        },
        cfTextColor: {
            displayName: 'Text Color',
            type: 'Text',
            group: 'style',
            description: 'The text color of the button.',
            defaultValue: 'rgba(255, 255, 255, 1)',
        },
        cfBackgroundColor: {
            displayName: 'Background color',
            type: 'Text',
            group: 'style',
            description: 'The background color of the button.',
            defaultValue: 'rgba(0, 0, 0, 1)',
        },
        cfPadding: {
            displayName: 'Padding',
            type: 'Text',
            group: 'style',
            description: 'The padding of the button.',
            defaultValue: '6px 12px 6px 12px',
        },
        cfWidth: {
            displayName: 'Width',
            type: 'Text',
            group: 'style',
            description: 'The width of the button.',
            defaultValue: 'fit-content',
        },
        cfHeight: {
            displayName: 'Height',
            type: 'Text',
            group: 'style',
            description: 'The height of the button.',
            defaultValue: 'fit-content',
        },
        cfTextAlign: {
            displayName: 'Text Align',
            type: 'Text',
            group: 'style',
            description: 'The text align of the button.',
            defaultValue: 'center',
        },
        label: {
            displayName: 'Text',
            type: 'Text',
            defaultValue: 'Button',
            validations: {
                bindingSourceType: ['entry', 'manual'],
            },
        },
        url: {
            displayName: 'URL',
            type: 'Hyperlink',
            validations: {
                bindingSourceType: ['entry', 'experience', 'manual'],
            },
        },
        target: {
            displayName: 'URL behavior',
            type: 'Text',
            defaultValue: '',
        },
    },
};

var css_248z$6 = ".cf-heading {\n  white-space: pre-line;\n}\n";
styleInject(css_248z$6);

const Heading = ({ children, className, text, type = 'h1', ...props }) => {
    const Tag = type;
    const classes = combineClasses('cf-heading', className);
    return (React.createElement(Tag, { className: classes, ...props }, text ? text : children));
};

const HeadingComponentDefinition = {
    id: CONTENTFUL_COMPONENTS$1.heading.id,
    name: CONTENTFUL_COMPONENTS$1.heading.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    builtInStyles: [
        'cfMargin',
        'cfPadding',
        'cfFontWeight',
        'cfLetterSpacing',
        'cfTextAlign',
        'cfTextColor',
        'cfTextTransform',
        'cfTextItalic',
        'cfTextUnderline',
        'cfWidth',
        'cfMaxWidth',
        'cfBackgroundColor',
        'cfBorder',
        'cfBorderRadius',
    ],
    tooltip: {
        description: 'Drop onto the canvas to add a heading.',
    },
    variables: {
        // Built-in style variables with default values changed
        cfHeight: {
            displayName: 'Height',
            type: 'Text',
            group: 'style',
            description: 'The height of the heading.',
            defaultValue: 'fit-content',
        },
        cfFontSize: {
            displayName: 'Font Size',
            type: 'Text',
            group: 'style',
            description: 'The font size of the heading.',
            defaultValue: '32px',
        },
        cfLineHeight: {
            displayName: 'Line Height',
            type: 'Text',
            group: 'style',
            description: 'The line height of the heading.',
            defaultValue: '48px',
        },
        cfTextBold: {
            displayName: 'Bold',
            type: 'Boolean',
            group: 'style',
            description: 'The text bold of the heading.',
            defaultValue: true,
        },
        // Component specific variables
        text: {
            displayName: 'Text',
            type: 'Text',
            description: 'The text to display in the heading.',
            defaultValue: 'Heading',
        },
        type: {
            displayName: 'HTML tag',
            type: 'Text',
            defaultValue: 'h1',
            group: 'style',
            description: 'Determines the HTML tag of the heading. Value can be h1, h2, h3, h4, h5, or h6.',
            validations: {
                in: [
                    { value: 'h1', displayName: 'H1' },
                    { value: 'h2', displayName: 'H2' },
                    { value: 'h3', displayName: 'H3' },
                    { value: 'h4', displayName: 'H4' },
                    { value: 'h5', displayName: 'H5' },
                    { value: 'h6', displayName: 'H6' },
                ],
            },
        },
    },
};

var css_248z$5 = ".cf-richtext {\n  white-space: pre-line;\n}\n\n.cf-richtext > *:first-child {\n  margin-top: 0;\n}\n\n.cf-richtext > *:last-child {\n  margin-bottom: 0;\n}\n";
styleInject(css_248z$5);

const RichText = ({ as = 'p', className, value, ...props }) => {
    const Tag = as;
    return (React.createElement("div", { className: combineClasses('cf-richtext', className), ...props }, documentToReactComponents(value, {
        renderNode: {
            [BLOCKS.PARAGRAPH]: (_node, children) => React.createElement(Tag, null, children),
        },
    })));
};

const RichTextComponentDefinition = {
    id: CONTENTFUL_COMPONENTS$1.richText.id,
    name: CONTENTFUL_COMPONENTS$1.richText.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    builtInStyles: [
        'cfMargin',
        'cfPadding',
        'cfFontWeight',
        'cfLetterSpacing',
        'cfTextTransform',
        'cfMaxWidth',
        'cfBackgroundColor',
        'cfBorder',
        'cfBorderRadius',
        'cfTextColor',
    ],
    tooltip: {
        description: 'Drop onto the canvas to add text with Rich text formatting options.',
    },
    variables: {
        // Built-in style variables with default values changed
        cfLineHeight: {
            displayName: 'Line Height',
            type: 'Text',
            group: 'style',
            description: 'The line height of the Rich text.',
            defaultValue: '24px',
        },
        cfTextAlign: {
            displayName: 'Text Align',
            type: 'Text',
            group: 'style',
            description: 'The text alignment of the Rich text.',
            defaultValue: 'center',
        },
        cfWidth: {
            displayName: 'Width',
            type: 'Text',
            group: 'style',
            description: 'The width of the Rich text.',
            defaultValue: 'fit-content',
        },
        cfHeight: {
            displayName: 'Height',
            type: 'Text',
            group: 'style',
            description: 'The height of the Rich text.',
            defaultValue: 'fit-content',
        },
        value: {
            displayName: 'Text',
            description: 'The text to display.',
            type: 'RichText',
            validations: {
                bindingSourceType: ['entry', 'manual'],
            },
            defaultValue: {
                nodeType: 'document',
                data: {},
                content: [
                    {
                        nodeType: 'paragraph',
                        data: {},
                        content: [
                            {
                                nodeType: 'text',
                                value: 'Rich text',
                                marks: [],
                                data: {},
                            },
                        ],
                    },
                ],
            },
        },
    },
};

var css_248z$4 = ".cf-text {\n  white-space: pre-line;\n}\n\n.cf-text-link .cf-text {\n  margin: 0;\n}\n";
styleInject(css_248z$4);

const Text = ({ as = 'p', children, className, value, url, target, onNavigate, onClick, ...props }) => {
    const Tag = as;
    if (url) {
        return (React.createElement("a", { className: combineClasses('cf-text-link', className), href: url, ...(target ? { target } : {}), ...props },
            React.createElement(Tag, { className: "cf-text" }, value ? value : children)));
    }
    const handleClick = (event) => {
        if (onNavigate && url) {
            event.preventDefault();
            onNavigate(url, target);
        }
        onClick && onClick(event);
    };
    return (React.createElement(Tag, { className: combineClasses('cf-text', className), onClick: handleClick, "data-url": url, ...(target ? { 'data-target': target } : {}), ...props }, value ? value : children));
};

const TextComponentDefinition = {
    id: CONTENTFUL_COMPONENTS$1.text.id,
    name: CONTENTFUL_COMPONENTS$1.text.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    builtInStyles: [
        'cfMargin',
        'cfPadding',
        'cfFontSize',
        'cfFontWeight',
        'cfLineHeight',
        'cfLetterSpacing',
        'cfTextColor',
        'cfTextAlign',
        'cfTextTransform',
        'cfTextBold',
        'cfTextItalic',
        'cfTextUnderline',
        'cfBackgroundColor',
        'cfBorder',
        'cfBorderRadius',
        'cfWidth',
        'cfMaxWidth',
    ],
    tooltip: {
        description: 'Drop onto the canvas to add plain text.',
    },
    variables: {
        cfHeight: {
            displayName: 'Height',
            type: 'Text',
            group: 'style',
            description: 'The height of the text.',
            defaultValue: 'fit-content',
        },
        value: {
            displayName: 'Text',
            description: 'The text to display. If not provided, children will be used instead.',
            type: 'Text',
            defaultValue: 'Text',
        },
        as: {
            displayName: 'HTML tag',
            description: 'Renders the text in a specific HTML tag.',
            type: 'Text',
            defaultValue: 'p',
            group: 'style',
            validations: {
                in: [
                    { value: 'p', displayName: 'p' },
                    { value: 'span', displayName: 'span' },
                    { value: 'div', displayName: 'div' },
                    { value: 'label', displayName: 'label' },
                    { value: 'caption', displayName: 'caption' },
                    { value: 'small', displayName: 'small' },
                    { value: 'strong', displayName: 'strong' },
                    { value: 'em', displayName: 'em' },
                ],
            },
        },
        url: {
            displayName: 'URL',
            type: 'Text',
            defaultValue: '',
            validations: {
                bindingSourceType: ['entry', 'experience', 'manual'],
            },
        },
        target: {
            displayName: 'URL behavior',
            type: 'Text',
            defaultValue: '',
        },
    },
};

var css_248z$3 = "@import url(https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,400;0,500;0,600;1,400;1,600&display=swap);\n\n:root {\n  /* All sizing comments based of 16px base body font size */\n\n  /* color */\n  --cf-color-white: #fff;\n  --cf-color-black: #000;\n  --cf-color-gray100: #f7f9fa;\n  --cf-color-gray400: #aec1cc;\n  --cf-color-gray400-rgb: 174, 193, 204;\n\n  /* spacing */\n  --cf-spacing-0: 0rem; /* 0px */\n  --cf-spacing-1: 0.125rem; /* 2px */\n  --cf-spacing-2: 0.25rem; /* 4px */\n  --cf-spacing-3: 0.375rem; /* 6px */\n  --cf-spacing-4: 0.5rem; /* 8px */\n  --cf-spacing-5: 0.625rem; /* 10px */\n  --cf-spacing-6: 0.75rem; /* 12px */\n  --cf-spacing-7: 0.875rem; /* 14px */\n  --cf-spacing-8: 1rem; /* 16px */\n  --cf-spacing-9: 1.25rem; /* 20px */\n  --cf-spacing-10: 1.5rem; /* 24px */\n  --cf-spacing-11: 1.75rem; /* 28px */\n  --cf-spacing-12: 2rem; /* 32px */\n  --cf-spacing-13: 2.25rem; /* 36px */\n\n  /* font-size */\n  --cf-text-xs: 0.75rem; /* 12px */\n  --cf-text-sm: 0.875rem; /* 14px */\n  --cf-text-base: 1rem; /* 16px */\n  --cf-text-lg: 1.125rem; /* 18px */\n  --cf-text-xl: 1.25rem; /* 20px */\n  --cf-text-2xl: 1.5rem; /* 24px */\n  --cf-text-3xl: 2rem; /* 32px */\n  --cf-text-4xl: 2.75rem; /* 44px */\n\n  /* font-weight */\n  --cf-font-light: 300;\n  --cf-font-normal: 400;\n  --cf-font-medium: 500;\n  --cf-font-semibold: 600;\n  --cf-font-bold: 700;\n  --cf-font-extra-bold: 800;\n  --cf-font-black: 900;\n\n  /* border-radius */\n  --cf-border-radius-none: 0px; /* none */\n  --cf-border-radius-sm: 0.125rem; /* 2px */\n  --cf-border-radius: 0.25rem; /* 4px */\n  --cf-border-radius-md: 0.375rem; /* 6px */\n  --cf-border-radius-lg: 0.5rem; /* 8px */\n  --cf-border-radius-xl: 0.75rem; /* 12px */\n  --cf-border-radius-2xl: 1rem; /* 16px */\n  --cf-border-radius-3xl: 1.5rem; /* 24px */\n  --cf-border-radius-full: 9999px; /* full */\n\n  /* font-family */\n  --cf-font-family-sans: Archivo, Helvetica, Arial, sans-serif;\n  --cf-font-family-serif: Georgia, Cambria, Times New Roman, Times, serif;\n\n  /* max widths */\n  --cf-max-width-full: 100%;\n\n  /* component specific colors */\n  --cf-button-bg: var(--cf-color-black);\n  --cf-button-color: var(--cf-color-white);\n  --cf-text-color: var(--cf-color-black);\n}\n\ndiv.cf-placeholder-wrapper {\n  /* Required for the absolute positioned icon to render in the center */\n  position: relative;\n}\n\nimg.cf-placeholder-image {\n  background-color: var(--cf-color-gray100);\n  outline-offset: -2px;\n  outline: 2px solid rgba(var(--cf-color-gray400-rgb), 0.5);\n  width: 100%;\n  height: 100%;\n}\n\nsvg.cf-placeholder-icon {\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  height: var(--cf-text-3xl);\n  width: var(--cf-text-3xl);\n  max-height: 100%;\n  max-width: 100%;\n}\n\nsvg.cf-placeholder-icon path {\n  fill: var(--cf-color-gray400);\n}\n";
styleInject(css_248z$3);

const Image = ({ className = '', src, cfImageAsset, ...props }) => {
    if (!cfImageAsset && !src) {
        return (React.createElement("div", { className: combineClasses('cf-placeholder-wrapper', className) },
            React.createElement("img", { className: 'cf-placeholder-image', src: "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAA", ...props }),
            React.createElement("svg", { className: "cf-placeholder-icon", fill: "none", viewBox: "0 0 17 16", xmlns: "http://www.w3.org/2000/svg" },
                React.createElement("path", { fill: "#fff", d: "M13.5 2h-10a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1m-10 1h10v4.836l-1.543-1.543a1 1 0 0 0-1.414 0L3.836 13H3.5zm10 10H5.25l6-6 2.25 2.25zm-7-5.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m0-2a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1" }))));
    }
    const imageClasses = combineClasses('cf-image', className);
    if (typeof cfImageAsset === 'string') {
        return React.createElement("img", { src: cfImageAsset, className: imageClasses, ...props });
    }
    if (cfImageAsset) {
        return (React.createElement("img", { src: cfImageAsset.url, srcSet: cfImageAsset.srcSet?.length ? cfImageAsset.srcSet?.join(', ') : undefined, sizes: cfImageAsset.sizes ? cfImageAsset.sizes : undefined, loading: cfImageAsset.loading, className: imageClasses, ...props }));
    }
    if (src) {
        return React.createElement("img", { src: src, className: imageClasses, ...props });
    }
};

const ImageComponentDefinition = {
    id: CONTENTFUL_COMPONENTS$1.image.id,
    name: CONTENTFUL_COMPONENTS$1.image.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    builtInStyles: ['cfMargin', 'cfPadding', 'cfImageAsset', 'cfImageOptions', 'cfBorderRadius'],
    tooltip: {
        description: 'Drop onto the canvas to upload an image.',
    },
    variables: {
        alt: {
            displayName: 'Alt text',
            type: 'Text',
            description: 'Alternative text for the image',
            validations: {
                bindingSourceType: ['entry', 'manual', 'asset'],
            },
        },
    },
};

/**
 * These modes are ONLY intended to be internally used within the context of
 * editing an experience inside of Contentful Studio. i.e. these modes
 * intentionally do not include preview/delivery modes.
 */
var StudioCanvasMode;
(function (StudioCanvasMode) {
    StudioCanvasMode["READ_ONLY"] = "readOnlyMode";
    StudioCanvasMode["EDITOR"] = "editorMode";
    StudioCanvasMode["NONE"] = "none";
})(StudioCanvasMode || (StudioCanvasMode = {}));
const CONTENTFUL_COMPONENTS = {
    section: {
        id: 'contentful-section',
        name: 'Section',
    },
    container: {
        id: 'contentful-container',
        name: 'Container',
    },
    columns: {
        id: 'contentful-columns',
        name: 'Columns',
    },
    singleColumn: {
        id: 'contentful-single-column',
        name: 'Column',
    },
    button: {
        id: 'contentful-button',
        name: 'Button',
    },
    heading: {
        id: 'contentful-heading',
        name: 'Heading',
    },
    image: {
        id: 'contentful-image',
        name: 'Image',
    },
    richText: {
        id: 'contentful-richText',
        name: 'Rich Text',
    },
    text: {
        id: 'contentful-text',
        name: 'Text',
    },
    divider: {
        id: 'contentful-divider',
        name: 'Divider',
    },
    carousel: {
        id: 'contentful-carousel',
        name: 'Carousel',
    },
};
const DEFAULT_IMAGE_WIDTH = '500px';
var PostMessageMethods;
(function (PostMessageMethods) {
    PostMessageMethods["REQUEST_ENTITIES"] = "REQUEST_ENTITIES";
    PostMessageMethods["REQUESTED_ENTITIES"] = "REQUESTED_ENTITIES";
})(PostMessageMethods || (PostMessageMethods = {}));
new Set(Object.values(CONTENTFUL_COMPONENTS).map((component) => component.id));

// These styles get added to every component, user custom or contentful provided
const builtInStyles = {
    cfVerticalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align left',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align right',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The vertical alignment of the section',
        defaultValue: 'center',
        displayName: 'Vertical alignment',
    },
    cfHorizontalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align top',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align bottom',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The horizontal alignment of the section',
        defaultValue: 'center',
        displayName: 'Horizontal alignment',
    },
    cfVisibility: {
        displayName: 'Visibility toggle',
        type: 'Boolean',
        group: 'style',
        defaultValue: true,
        description: 'The visibility of the component',
    },
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the section',
        defaultValue: '0 0 0 0',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the section',
        defaultValue: '0 0 0 0',
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the section',
        defaultValue: 'rgba(0, 0, 0, 0)',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the section',
        defaultValue: '100%',
    },
    cfHeight: {
        displayName: 'Height',
        type: 'Text',
        group: 'style',
        description: 'The height of the section',
        defaultValue: 'fit-content',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the section',
        defaultValue: 'none',
    },
    cfFlexDirection: {
        displayName: 'Direction',
        type: 'Text',
        group: 'style',
        description: 'The orientation of the section',
        defaultValue: 'column',
    },
    cfFlexReverse: {
        displayName: 'Reverse Direction',
        type: 'Boolean',
        group: 'style',
        description: 'Toggle the flex direction to be reversed',
        defaultValue: false,
    },
    cfFlexWrap: {
        displayName: 'Wrap objects',
        type: 'Text',
        group: 'style',
        description: 'Wrap objects',
        defaultValue: 'nowrap',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the section',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the section',
        defaultValue: '0px',
    },
    cfHyperlink: {
        displayName: 'URL',
        type: 'Hyperlink',
        defaultValue: '',
        validations: {
            format: 'URL',
            bindingSourceType: ['entry', 'experience', 'manual'],
        },
        description: 'hyperlink for section or container',
    },
    cfOpenInNewTab: {
        displayName: 'URL behaviour',
        type: 'Boolean',
        defaultValue: false,
        description: 'Open in new tab',
    },
};
const optionalBuiltInStyles = {
    cfFontSize: {
        displayName: 'Font Size',
        type: 'Text',
        group: 'style',
        description: 'The font size of the element',
        defaultValue: '16px',
    },
    cfFontWeight: {
        validations: {
            in: [
                {
                    value: '400',
                    displayName: 'Normal',
                },
                {
                    value: '500',
                    displayName: 'Medium',
                },
                {
                    value: '600',
                    displayName: 'Semi Bold',
                },
            ],
        },
        displayName: 'Font Weight',
        type: 'Text',
        group: 'style',
        description: 'The font weight of the element',
        defaultValue: '400',
    },
    cfImageAsset: {
        displayName: 'Image',
        type: 'Media',
        description: 'Image to display',
        validations: {
            bindingSourceType: ['entry', 'asset', 'manual'],
        },
    },
    cfImageOptions: {
        displayName: 'Image options',
        type: 'Object',
        group: 'style',
        defaultValue: {
            width: DEFAULT_IMAGE_WIDTH,
            height: '100%',
            targetSize: DEFAULT_IMAGE_WIDTH,
        },
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the element',
        defaultValue: 'rgba(0, 0, 0, 0)',
    },
    cfBackgroundImageUrl: {
        displayName: 'Background image',
        type: 'Media',
        description: 'Background image for component',
        validations: {
            bindingSourceType: ['entry', 'asset', 'manual'],
        },
    },
    cfBackgroundImageOptions: {
        displayName: 'Background image options',
        type: 'Object',
        group: 'style',
        defaultValue: {
            scaling: 'fill',
            alignment: 'left top',
            targetSize: '2000px',
        },
    },
    cfBorderRadius: {
        displayName: 'Border Radius',
        type: 'Text',
        group: 'style',
        description: 'The border radius of the section',
        defaultValue: '0px',
    },
    cfLineHeight: {
        displayName: 'Line Height',
        type: 'Text',
        group: 'style',
        description: 'The line height of the element',
        defaultValue: '20px',
    },
    cfLetterSpacing: {
        displayName: 'Letter Spacing',
        type: 'Text',
        group: 'style',
        description: 'The letter spacing of the element',
        defaultValue: '0px',
    },
    cfTextColor: {
        displayName: 'Text Color',
        type: 'Text',
        group: 'style',
        description: 'The text color of the element',
        defaultValue: 'rgba(0, 0, 0, 1)',
    },
    cfTextAlign: {
        validations: {
            in: [
                {
                    value: 'left',
                    displayName: 'Align left',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'right',
                    displayName: 'Align right',
                },
            ],
        },
        displayName: 'Text Align',
        type: 'Text',
        group: 'style',
        description: 'The text alignment of the element',
        defaultValue: 'left',
    },
    cfTextTransform: {
        validations: {
            in: [
                {
                    value: 'none',
                    displayName: 'Normal',
                },
                {
                    value: 'capitalize',
                    displayName: 'Capitalize',
                },
                {
                    value: 'uppercase',
                    displayName: 'Uppercase',
                },
                {
                    value: 'lowercase',
                    displayName: 'Lowercase',
                },
            ],
        },
        displayName: 'Text Transform',
        type: 'Text',
        group: 'style',
        description: 'The text transform of the element',
        defaultValue: 'none',
    },
    cfTextBold: {
        displayName: 'Bold',
        type: 'Boolean',
        group: 'style',
        description: 'The text bold of the element',
        defaultValue: false,
    },
    cfTextItalic: {
        displayName: 'Italic',
        type: 'Boolean',
        group: 'style',
        description: 'The text italic of the element',
        defaultValue: false,
    },
    cfTextUnderline: {
        displayName: 'Underline',
        type: 'Boolean',
        group: 'style',
        description: 'The text underline of the element',
        defaultValue: false,
    },
};
const sectionBuiltInStyles = {
    ...builtInStyles,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
};
const containerBuiltInStyles = {
    ...builtInStyles,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the container',
        // Note: The UI overwrites '0 Auto 0 Auto' as the default value for top-level containers
        defaultValue: '0 0 0 0',
    },
    cfMaxWidth: {
        displayName: 'Max Width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the section',
        defaultValue: '1192px',
    },
};
const dividerBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the divider',
        defaultValue: '20px 0px 20px 0px',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the divider',
        defaultValue: '100%',
    },
    cfHeight: {
        displayName: 'Height',
        type: 'Text',
        group: 'style',
        description: 'The height of the divider',
        defaultValue: '1px',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the divider',
        defaultValue: 'none',
    },
    cfBackgroundColor: {
        displayName: 'Background color',
        type: 'Text',
        group: 'style',
        description: 'The background color of the divider',
        defaultValue: 'rgba(204, 204, 204, 1)',
    },
};
const singleColumnBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundColor: optionalBuiltInStyles.cfBackgroundColor,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfVerticalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align left',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align right',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The vertical alignment of the column',
        defaultValue: 'center',
        displayName: 'Vertical alignment',
    },
    cfHorizontalAlignment: {
        validations: {
            in: [
                {
                    value: 'start',
                    displayName: 'Align top',
                },
                {
                    value: 'center',
                    displayName: 'Align center',
                },
                {
                    value: 'end',
                    displayName: 'Align bottom',
                },
            ],
        },
        type: 'Text',
        group: 'style',
        description: 'The horizontal alignment of the column',
        defaultValue: 'center',
        displayName: 'Horizontal alignment',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the column',
        defaultValue: '0 0 0 0',
    },
    cfFlexDirection: {
        displayName: 'Direction',
        type: 'Text',
        group: 'style',
        description: 'The orientation of the column',
        defaultValue: 'column',
    },
    cfFlexWrap: {
        displayName: 'Wrap objects',
        type: 'Text',
        group: 'style',
        description: 'Wrap objects',
        defaultValue: 'nowrap',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the column',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the column',
        defaultValue: '0px',
    },
    cfColumnSpan: {
        type: 'Text',
        defaultValue: '6',
        group: 'style',
    },
    cfColumnSpanLock: {
        type: 'Boolean',
        defaultValue: false,
        group: 'style',
    },
};
const columnsBuiltInStyles = {
    cfVisibility: builtInStyles.cfVisibility,
    cfBorderRadius: optionalBuiltInStyles.cfBorderRadius,
    cfBackgroundColor: optionalBuiltInStyles.cfBackgroundColor,
    cfBackgroundImageUrl: optionalBuiltInStyles.cfBackgroundImageUrl,
    cfBackgroundImageOptions: optionalBuiltInStyles.cfBackgroundImageOptions,
    cfMargin: {
        displayName: 'Margin',
        type: 'Text',
        group: 'style',
        description: 'The margin of the columns',
        // Note: The UI overwrites '0 Auto 0 Auto' as the default value for top-level columns
        defaultValue: '0 0 0 0',
    },
    cfWidth: {
        displayName: 'Width',
        type: 'Text',
        group: 'style',
        description: 'The width of the columns',
        defaultValue: '100%',
    },
    cfMaxWidth: {
        displayName: 'Max width',
        type: 'Text',
        group: 'style',
        description: 'The max-width of the columns',
        defaultValue: '1192px',
    },
    cfPadding: {
        displayName: 'Padding',
        type: 'Text',
        group: 'style',
        description: 'The padding of the columns',
        defaultValue: '10px 10px 10px 10px',
    },
    cfBorder: {
        displayName: 'Border',
        type: 'Text',
        group: 'style',
        description: 'The border of the columns',
        defaultValue: '0px solid rgba(0, 0, 0, 0)',
    },
    cfGap: {
        displayName: 'Gap',
        type: 'Text',
        group: 'style',
        description: 'The spacing between the elements of the columns',
        defaultValue: '10px 10px',
    },
    cfColumns: {
        type: 'Text',
        defaultValue: '[6,6]',
        group: 'style',
    },
    cfWrapColumns: {
        type: 'Boolean',
        defaultValue: false,
        group: 'style',
    },
    cfWrapColumnsCount: {
        type: 'Text',
        defaultValue: '2',
        group: 'style',
    },
};

// If more than one version is supported, use z.union
const SchemaVersions = z.literal('2023-09-28');
// Keep deprecated versions here just for reference
z.union([
    z.literal('2023-08-23'),
    z.literal('2023-07-26'),
    z.literal('2023-06-27'),
]);

const DefinitionPropertyTypeSchema = z.enum([
    'Text',
    'RichText',
    'Number',
    'Date',
    'Boolean',
    'Location',
    'Media',
    'Object',
    'Hyperlink',
    'Array',
    'Link',
]);
const DefinitionPropertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,32}$/, { message: 'Property needs to match: /^[a-zA-Z0-9-_]{1,32}$/' });
const PrimitiveValueSchema = z.union([
    z.string(),
    z.boolean(),
    z.number(),
    z.record(z.any(), z.any()),
    z.undefined(),
]);
const uuidKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,21}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,21}$/' });
/**
 * Property keys for imported components have a limit of 32 characters (to be implemented) while
 * property keys for patterns have a limit of 54 characters (<32-char-variabl-name>_<21-char-nanoid-id>).
 * Because we cannot distinguish between the two in the componentTree, we will use the larger limit for both.
 */
const propertyKeySchema = z
    .string()
    .regex(/^[a-zA-Z0-9-_]{1,54}$/, { message: 'Does not match /^[a-zA-Z0-9-_]{1,54}$/' });
const DataSourceSchema = z.record(uuidKeySchema, z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.enum(['Entry', 'Asset']),
    }),
}));
const ValuesByBreakpointSchema = z.record(z.lazy(() => PrimitiveValueSchema));
const DesignValueSchema = z
    .object({
    type: z.literal('DesignValue'),
    valuesByBreakpoint: ValuesByBreakpointSchema,
})
    .strict();
const BoundValueSchema = z
    .object({
    type: z.literal('BoundValue'),
    path: z.string(),
})
    .strict();
const HyperlinkValueSchema = z
    .object({
    type: z.literal('HyperlinkValue'),
    linkTargetKey: z.string(),
    /** Allows to override parts of the URL, e.g. the locale */
    overrides: z.object({}).optional(),
})
    .strict();
const UnboundValueSchema = z
    .object({
    type: z.literal('UnboundValue'),
    key: z.string(),
})
    .strict();
const ComponentValueSchema = z
    .object({
    type: z.literal('ComponentValue'),
    key: z.string(),
})
    .strict();
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const NoValueSchema = z.object({ type: z.literal('NoValue') }).strict();
const ComponentPropertyValueSchema = z.discriminatedUnion('type', [
    DesignValueSchema,
    BoundValueSchema,
    UnboundValueSchema,
    HyperlinkValueSchema,
    ComponentValueSchema,
    NoValueSchema,
]);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const VariableMappingSchema = z.object({
    patternPropertyDefinitionId: propertyKeySchema,
    type: z.literal('ContentTypeMapping'),
    pathsByContentType: z.record(z.string(), z.object({ path: z.string() })),
});
const VariableMappingsSchema = z.record(propertyKeySchema, VariableMappingSchema);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const PatternPropertyDefinitionSchema = z.object({
    defaultValue: z
        .record(z.string(), z.object({
        sys: z.object({
            type: z.literal('Link'),
            id: z.string(),
            linkType: z.enum(['Entry']),
        }),
    }))
        .optional(),
    contentTypes: z.record(z.string(), z.object({
        sys: z.object({
            type: z.literal('Link'),
            id: z.string(),
            linkType: z.enum(['ContentType']),
        }),
    })),
});
const PatternPropertyDefinitionsSchema = z.record(propertyKeySchema, PatternPropertyDefinitionSchema);
// TODO: finalize schema structure before release
// https://contentful.atlassian.net/browse/LUMOS-523
const PatternPropertySchema = z.object({
    type: z.literal('BoundValue'),
    path: z.string(),
    contentType: z.string(),
});
const PatternPropertysSchema = z.record(propertyKeySchema, PatternPropertySchema);
const BreakpointSchema = z
    .object({
    id: propertyKeySchema,
    query: z.string().regex(/^\*$|^<[0-9*]+px$/),
    previewSize: z.string(),
    displayName: z.string(),
    displayIcon: z.enum(['desktop', 'tablet', 'mobile']).optional(),
})
    .strict();
const UnboundValuesSchema = z.record(uuidKeySchema, z.object({
    value: PrimitiveValueSchema,
}));
const ComponentTreeNodeIdSchema = z
    .string()
    .regex(/^[a-zA-Z0-9]{1,8}$/, { message: 'Does not match /^[a-zA-Z0-9]{1,8}$/' });
// Use helper schema to define a recursive schema with its type correctly below
const BaseComponentTreeNodeSchema = z.object({
    id: ComponentTreeNodeIdSchema.optional(),
    definitionId: DefinitionPropertyKeySchema,
    displayName: z.string().optional(),
    slotId: z.string().optional(),
    variables: z.record(propertyKeySchema, ComponentPropertyValueSchema),
    patternProperties: PatternPropertysSchema.optional(),
});
const ComponentTreeNodeSchema = BaseComponentTreeNodeSchema.extend({
    children: z.lazy(() => ComponentTreeNodeSchema.array()),
});
const BindingSourceTypeEnumSchema = z
    .array(z.enum(['entry', 'asset', 'manual', 'experience']))
    .nonempty();
const ComponentVariableSchema = z.object({
    displayName: z.string().optional(),
    type: DefinitionPropertyTypeSchema,
    description: z.string().optional(),
    group: z.string().optional(),
    defaultValue: PrimitiveValueSchema.or(ComponentPropertyValueSchema).optional(),
    validations: z
        .object({
        bindingSourceType: BindingSourceTypeEnumSchema.optional(),
        required: z.boolean().optional(),
        format: z.literal('URL').optional(),
        in: z
            .array(z.object({
            value: z.union([z.string(), z.number()]),
            displayName: z.string().optional(),
        }))
            .optional(),
    })
        .optional(),
});
const ComponentVariablesSchema = z.record(z.string().regex(/^[a-zA-Z0-9-_]{1,54}$/), // Here the key is <variableName>_<nanoidId> so we need to allow for a longer length
ComponentVariableSchema);
const THUMBNAIL_IDS = [
    'columns',
    'columnsPlusRight',
    'imagesSquare',
    'subtitles',
    'rowsPlusBottom',
    'userRectangle',
    'textbox',
    'monitorPlay',
    'article',
    'table',
    'star',
    'heartStraight',
    'frameCorners',
    'rows',
    'dotsThreeOutline',
    'listDashes',
    'checkerBoard',
    'gridFour',
    'slideshow',
    'diamondsFour',
    'cards',
    'textColumns',
    'duplex',
];
const ComponentSettingsSchema = z.object({
    variableDefinitions: ComponentVariablesSchema,
    thumbnailId: z.enum(THUMBNAIL_IDS).optional(),
    category: z.string().max(50, 'Category must contain at most 50 characters').optional(),
    variableMappings: VariableMappingsSchema.optional(),
    patternPropertyDefinitions: PatternPropertyDefinitionsSchema.optional(),
});
const UsedComponentsSchema = z.array(z.object({
    sys: z.object({
        type: z.literal('Link'),
        id: z.string(),
        linkType: z.literal('Entry'),
    }),
}));
const breakpointsRefinement = (value, ctx) => {
    if (!value.length || value[0].query !== '*') {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `The first breakpoint should include the following attributes: { "query": "*" }`,
        });
    }
    const hasDuplicateIds = value.some((currentBreakpoint, currentBreakpointIndex) => {
        // check if the current breakpoint id is found in the rest of the array
        const breakpointIndex = value.findIndex((breakpoint) => breakpoint.id === currentBreakpoint.id);
        return breakpointIndex !== currentBreakpointIndex;
    });
    if (hasDuplicateIds) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Breakpoint IDs must be unique`,
        });
    }
    // Extract the queries boundary by removing the special characters around it
    const queries = value.map((bp) => bp.query === '*' ? bp.query : parseInt(bp.query.replace(/px|<|>/, '')));
    // sort updates queries array in place so we need to create a copy
    const originalQueries = [...queries];
    queries.sort((q1, q2) => {
        if (q1 === '*') {
            return -1;
        }
        if (q2 === '*') {
            return 1;
        }
        return q1 > q2 ? -1 : 1;
    });
    if (originalQueries.join('') !== queries.join('')) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Breakpoints should be ordered from largest to smallest pixel value`,
        });
    }
};
const ComponentTreeSchema = z
    .object({
    breakpoints: z.array(BreakpointSchema).superRefine(breakpointsRefinement),
    children: z.array(ComponentTreeNodeSchema),
    schemaVersion: SchemaVersions,
})
    .strict();
const localeWrapper = (fieldSchema) => z.record(z.string(), fieldSchema);
z.object({
    componentTree: localeWrapper(ComponentTreeSchema),
    dataSource: localeWrapper(DataSourceSchema),
    unboundValues: localeWrapper(UnboundValuesSchema),
    usedComponents: localeWrapper(UsedComponentsSchema).optional(),
    componentSettings: localeWrapper(ComponentSettingsSchema).optional(),
});

z.object({
    id: DefinitionPropertyKeySchema,
    variables: z.record(DefinitionPropertyKeySchema, ComponentVariableSchema.extend({
        defaultValue: PrimitiveValueSchema.optional(),
    }).superRefine((val, ctx) => {
        switch (val.type) {
            case 'Array':
                if (typeof val.defaultValue !== 'undefined') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Array" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Boolean':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'boolean') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a boolean when type is "Boolean" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Date':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Date" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Hyperlink':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Hyperlink" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Link':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue is not supported for "Link" type for ${ctx.path.join('.')}`,
                        fatal: false,
                    });
                }
                break;
            case 'Location':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Location" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Media':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Media" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Number':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'number') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a number when type is "Number" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Object':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "Object" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'RichText':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'object') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be an object when type is "RichText" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
            case 'Text':
                if (typeof val.defaultValue !== 'undefined' && typeof val.defaultValue !== 'string') {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: `defaultValue must be a string when type is "Text" for ${ctx.path.join('.')}, got ${typeof val.defaultValue} instead`,
                        fatal: false,
                    });
                }
                break;
        }
    })),
});

var CodeNames;
(function (CodeNames) {
    CodeNames["Type"] = "type";
    CodeNames["Required"] = "required";
    CodeNames["Unexpected"] = "unexpected";
    CodeNames["Regex"] = "regex";
    CodeNames["In"] = "in";
    CodeNames["Size"] = "size";
    CodeNames["Custom"] = "custom";
})(CodeNames || (CodeNames = {}));

const CF_DEBUG_KEY = 'cf_debug';
class DebugLogger {
    constructor() {
        // Public methods for logging
        this.error = this.logger('error');
        this.warn = this.logger('warn');
        this.log = this.logger('log');
        this.debug = this.logger('debug');
        if (typeof localStorage === 'undefined') {
            this.enabled = false;
            return;
        }
        // Default to checking localStorage for the debug mode on initialization if in browser
        this.enabled = localStorage.getItem(CF_DEBUG_KEY) === 'true';
    }
    static getInstance() {
        if (this.instance === null) {
            this.instance = new DebugLogger();
        }
        return this.instance;
    }
    getEnabled() {
        return this.enabled;
    }
    setEnabled(enabled) {
        this.enabled = enabled;
        if (typeof localStorage === 'undefined') {
            return;
        }
        if (enabled) {
            localStorage.setItem(CF_DEBUG_KEY, 'true');
        }
        else {
            localStorage.removeItem(CF_DEBUG_KEY);
        }
    }
    // Log method for different levels (error, warn, log)
    logger(level) {
        return (...args) => {
            if (this.enabled) {
                console[level]('[cf-experiences-sdk]', ...args);
            }
        };
    }
}
DebugLogger.instance = null;
DebugLogger.getInstance();

var VisualEditorMode;
(function (VisualEditorMode) {
    VisualEditorMode["LazyLoad"] = "lazyLoad";
    VisualEditorMode["InjectScript"] = "injectScript";
})(VisualEditorMode || (VisualEditorMode = {}));

var css_248z$2 = ".contentful-container {\n  position: relative;\n  display: flex;\n  box-sizing: border-box;\n  pointer-events: all;\n}\n\n.contentful-container::-webkit-scrollbar {\n  display: none; /* Safari and Chrome */\n}\n\n.cf-single-column-wrapper {\n  position: relative;\n}\n\n.cf-container-wrapper {\n  position: relative;\n  width: 100%;\n}\n\n.contentful-container:after {\n  content: '';\n  display: block;\n  position: absolute;\n  pointer-events: none;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  overflow-x: clip;\n  font-family: var(--exp-builder-font-stack-primary);\n  font-size: 12px;\n  color: var(--exp-builder-gray400);\n  z-index: 1;\n}\n\n.contentful-section-label:after {\n  content: 'Section';\n}\n\n.contentful-container-label:after {\n  content: 'Container';\n}\n\n/* used by ContentfulSectionAsHyperlink.tsx */\n\n.contentful-container-link,\n.contentful-container-link:active,\n.contentful-container-link:visited,\n.contentful-container-link:hover,\n.contentful-container-link:read-write,\n.contentful-container-link:focus-visible {\n  color: inherit;\n  text-decoration: unset;\n  outline: unset;\n}\n";
styleInject(css_248z$2);

const Flex = forwardRef(({ id, children, onMouseEnter, onMouseUp, onMouseLeave, onMouseDown, onClick, flex, flexBasis, flexShrink, flexDirection, gap, justifyContent, justifyItems, justifySelf, alignItems, alignSelf, alignContent, order, flexWrap, flexGrow, className, cssStyles, ...props }, ref) => {
    return (React.createElement("div", { id: id, ref: ref, style: {
            display: 'flex',
            flex,
            flexBasis,
            flexShrink,
            flexDirection,
            gap,
            justifyContent,
            justifyItems,
            justifySelf,
            alignItems,
            alignSelf,
            alignContent,
            order,
            flexWrap,
            flexGrow,
            ...cssStyles,
        }, className: className, onMouseEnter: onMouseEnter, onMouseUp: onMouseUp, onMouseDown: onMouseDown, onMouseLeave: onMouseLeave, onClick: onClick, ...props }, children));
});
Flex.displayName = 'Flex';

/* eslint-disable */ /* TODO: fix eslint errors */
const ContentfulContainerAsHyperlink = (props) => {
    const { cfHyperlink, cfOpenInNewTab, editorMode, className, children } = props;
    if (editorMode === false) {
        let anchorTagProps = {};
        if (cfOpenInNewTab) {
            anchorTagProps = {
                target: '_blank',
                rel: 'noopener noreferrer',
            };
        }
        return (React.createElement("a", { className: combineClasses(className, 'contentful-container', 'contentful-container-link'), href: cfHyperlink, ...anchorTagProps }, children));
    }
    const { renderDropzone, node } = props;
    const stopPropagationInEditorMode = (e) => {
        e.stopPropagation();
        e.preventDefault();
    };
    return renderDropzone(node, {
        ['data-test-id']: 'contentful-container',
        className: combineClasses(className, 'contentful-container', 'contentful-container-link'),
        zoneId: node.data.id,
        WrapperComponent: 'a',
        onClick: stopPropagationInEditorMode,
    });
};

/* eslint-disable */
const ContentfulContainer = (props) => {
    const { className, editorMode, children, cfHyperlink } = props;
    if (cfHyperlink) {
        return React.createElement(ContentfulContainerAsHyperlink, { ...props }, children);
    }
    if (editorMode === false) {
        return (React.createElement(Flex, { "data-test-id": "contentful-container", className: combineClasses(className, 'contentful-container') }, children));
    }
    // Extract properties that are only available in editor mode
    const { renderDropzone, node, dragProps = {}, ...editorModeProps } = props;
    const isEmpty = !node.children.length;
    const isSection = node.data.blockId === CONTENTFUL_COMPONENTS$1.section.id;
    return renderDropzone(node, {
        ...editorModeProps,
        ['data-test-id']: 'contentful-container',
        id: 'ContentfulContainer',
        className: combineClasses('contentful-container', className, isEmpty ? (isSection ? 'contentful-section-label' : 'contentful-container-label') : ''),
        WrapperComponent: Flex,
        dragProps,
    });
};

const containerDefinition = {
    id: CONTENTFUL_COMPONENTS$1.container.id,
    name: CONTENTFUL_COMPONENTS$1.container.name,
    category: CONTENTFUL_COMPONENT_CATEGORY,
    children: true,
    variables: containerBuiltInStyles,
    tooltip: {
        description: 'Create a new area or pattern within your page layout by dragging a container onto the canvas. Other components and patterns can be added into a container.',
    },
};
const sectionDefinition = {
    id: CONTENTFUL_COMPONENTS$1.section.id,
    name: CONTENTFUL_COMPONENTS$1.section.name,
    category: CONTENTFUL_COMPONENT_CATEGORY,
    children: true,
    variables: sectionBuiltInStyles,
    tooltip: {
        description: 'Create a new full width section of your experience by dragging this component onto the canvas. Other components and patterns can be added into a section.',
    },
};

var css_248z$1 = ".cf-divider {\n  display: contents;\n  position: relative;\n  width: 100%;\n  height: 100%;\n}\n\n.cf-divider hr {\n  border: none;\n}\n\n/* For the editor mode add this 10px tolerance to make it easier picking up the divider component.\n * Using the DND zone as precondition makes sure that we don't render this pseudo element in delivery. */\n\n[data-ctfl-zone-id='root'] .cf-divider::before {\n  content: \"\";\n  position: absolute;\n  top: -5px;\n  left: -5px;\n  bottom: -5px;\n  right: -5px;\n  pointer-events: all;\n}\n";
styleInject(css_248z$1);

const ContentfulDivider = ({ className = '', 
// We have to exclude this explicitly from rendering as withComponentWrapper is not used
dragProps: _, ...props }) => {
    return (React.createElement("div", { className: "cf-divider", ...props },
        React.createElement("hr", { className: className })));
};

const dividerDefinition = {
    id: CONTENTFUL_COMPONENTS$1.divider.id,
    name: CONTENTFUL_COMPONENTS$1.divider.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    children: false,
    variables: dividerBuiltInStyles,
    tooltip: {
        description: 'Drop onto the canvas to add a divider.',
    },
};

var css_248z = ".cf-columns {\n  display: flex;\n  gap: 24px;\n  grid-template-columns: repeat(12, 1fr);\n  flex-direction: column;\n  min-height: 0; /* NEW */\n  min-width: 0; /* NEW; needed for Firefox */\n}\n\n@media (min-width: 768px) {\n  .cf-columns {\n    display: grid;\n  }\n}\n\n.cf-single-column-wrapper {\n  position: relative;\n  display: flex;\n}\n\n.cf-single-column {\n  pointer-events: all;\n}\n\n.cf-single-column-wrapper:after {\n  content: '';\n  display: block;\n  position: absolute;\n  pointer-events: none;\n  top: 0;\n  left: 0;\n  right: 0;\n  bottom: 0;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  overflow-x: clip;\n  font-family: var(--exp-builder-font-stack-primary);\n  font-size: 12px;\n  color: var(--exp-builder-gray400);\n  z-index: 1;\n}\n\n.cf-single-column-label:after {\n  content: 'Column';\n}\n";
styleInject(css_248z);

const ColumnWrapper = forwardRef((props, ref) => {
    return (React.createElement("div", { ref: ref, ...props, style: {
            ...(props.style || {}),
            display: 'grid',
            gridTemplateColumns: 'repeat(12, [col-start] 1fr)',
        } }, props.children));
});
ColumnWrapper.displayName = 'ColumnWrapper';
const Columns = (props) => {
    const { editorMode, className, children } = props;
    if (!editorMode) {
        return (React.createElement(ColumnWrapper, { className: combineClasses(className, 'cf-columns') }, children));
    }
    const { node, renderDropzone, dragProps = {}, ...rest } = props;
    return renderDropzone(node, {
        ...rest,
        ['data-test-id']: 'contentful-columns',
        id: 'ContentfulContainer',
        className: combineClasses('cf-columns', className),
        WrapperComponent: ColumnWrapper,
        dragProps,
    });
};

const SingleColumn = (props) => {
    const { className, editorMode, children } = props;
    if (editorMode === false) {
        return React.createElement(Flex, { className: className }, children);
    }
    const { renderDropzone, node, className: _className, dragProps = {}, cfColumnSpan, editorMode: edit, ...editorProps } = props;
    const isEmpty = !node.children.length;
    return renderDropzone(node, {
        ['data-test-id']: 'contentful-single-column',
        id: 'ContentfulSingleColumn',
        className: combineClasses('cf-single-column-wrapper', className, isEmpty ? 'cf-single-column-label' : ''),
        WrapperComponent: Flex,
        dragProps,
        ...editorProps,
    });
};

const columnsDefinition = {
    id: CONTENTFUL_COMPONENTS$1.columns.id,
    name: CONTENTFUL_COMPONENTS$1.columns.name,
    category: CONTENTFUL_COMPONENT_CATEGORY,
    children: true,
    variables: columnsBuiltInStyles,
    tooltip: {
        description: 'Add columns to a container to create your desired layout and ensure that the experience is responsive across different screen sizes.',
    },
};
const singleColumnDefinition = {
    id: CONTENTFUL_COMPONENTS$1.singleColumn.id,
    name: CONTENTFUL_COMPONENTS$1.singleColumn.name,
    category: CONTENTFUL_COMPONENT_CATEGORY,
    children: true,
    variables: singleColumnBuiltInStyles,
};

const assemblyStyle = { display: 'contents' };
// Feel free to do any magic as regards variable definitions for assemblies
// Or if this isn't necessary by the time we figure that part out, we can bid this part farewell
const Assembly = (props) => {
    if (props.editorMode) {
        const { node, dragProps, ...editorModeProps } = props;
        return props.renderDropzone(node, {
            ...editorModeProps,
            ['data-test-id']: 'contentful-assembly',
            className: props.className,
            dragProps,
        });
    }
    // Using a display contents so assembly content/children
    // can appear as if they are direct children of the div wrapper's parent
    return React.createElement("div", { "data-test-id": "assembly", ...props, style: assemblyStyle });
};

const Carousel = ({ className, children, ...props }) => {
    return (React.createElement("div", { className: combineClasses(className, 'cf-carousel'), ...props }, children));
};

const carouselDefinition = {
    id: CONTENTFUL_COMPONENTS$1.carousel.id,
    name: CONTENTFUL_COMPONENTS$1.carousel.name,
    category: CONTENTFUL_DEFAULT_CATEGORY,
    children: true,
    builtInStyles: [
        'cfPadding',
        'cfMargin',
        'cfHeight',
        'cfWidth',
        'cfMaxWidth',
        'cfBorderRadius',
        'cfBackgroundColor',
        'cfBorder',
    ],
    variables: {},
    tooltip: {
        description: 'Drop onto the canvas to add a carousel.',
    },
};

export { Assembly, Button, ButtonComponentDefinition, Carousel, Columns, ContentfulContainer, ContentfulDivider, Heading, HeadingComponentDefinition, Image, ImageComponentDefinition, RichText, RichTextComponentDefinition, SingleColumn, Text, TextComponentDefinition, carouselDefinition, columnsDefinition, containerDefinition, dividerDefinition, sectionDefinition, singleColumnDefinition };
//# sourceMappingURL=index.js.map
